import { FunctionComponent, PropsWithChildren, useState } from "react";
import { Outlet } from "react-router-dom";

import {
  Box,
  CssBaseline,
  Paper as MuiPaper,
  useTheme,
  styled,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { spacing } from "@mui/system";

import { DndProvider } from "react-dnd";

import { HTML5Backend } from "react-dnd-html5-backend";

import GlobalStyle from "../theme/globalStyles/GlobalStyle";

import { MaterialAppThemeProvider as MuiThemeProvider } from "../theme";

import { PAGES } from "../config/pages";

import { SidebarItemsType } from "../theme/types/sidebar";

import Navbar from "./Frame/navbar/Navbar";

import Sidebar from "./Frame/sidebar/Sidebar";
import Footer from "./Frame/Footer";
import Settings from "./Frame/Settings";

import { IcIcon } from "./IcIcon";
import { ViewScopeGuard } from "./ScopeGuard";
import { ErrorBoundary } from "./ErrorBoundry";
import { LicenseAgreement } from "./LicenseAgreement/LicenseAgreement";
import { SubscriptionGuard } from "./SubscriptionGuard";
import { OrgStatusGuard } from "./OrgStatusGuard";

const pagesSection: SidebarItemsType[] = PAGES.map(
  ({ link, icon, title, iconProps, scope, newTab }) => {
    return {
      icon: (_p: PropsWithChildren<any>) => {
        return <IcIcon {...(iconProps || {})} icon={icon}></IcIcon>;
      },
      title,
      href: link,
      scope,
      newTab,
    } as SidebarItemsType;
  }
);
const dashboardItems = [
  {
    title: "Simpallo",
    pages: pagesSection,
  },
];
const drawerWidth = 258;

const Root = styled(Box)`
  display: flex;
  height: 100%;
  min-height: 100vh;
  max-height: 100vh;
  overflow: hidden;
`;

const Drawer = styled(Box)`
  ${(props) => props.theme.breakpoints.up("md")} {
    width: ${drawerWidth}px;
    flex-shrink: 0;
  }
`;

const AppContent = styled(Box)`
  flex: 1 1 0%;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  overflow: hidden;
`;

const Paper = styled(MuiPaper)(spacing);

const MainContent = styled(Paper)`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  background: ${(props) => props.theme.palette.background.default};

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }
`;
const FrameCore: React.FC<PropsWithChildren> = ({ children: _children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme?.breakpoints?.up("lg"));

  return (
    <Root>
      <DndProvider backend={HTML5Backend}>
        <CssBaseline />
        <GlobalStyle />
        <Drawer>
          <Box sx={{ display: { xs: "block", lg: "none" } }}>
            <Sidebar
              PaperProps={{ style: { width: drawerWidth } }}
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              items={dashboardItems}
            />
          </Box>
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <Sidebar
              PaperProps={{ style: { width: drawerWidth } }}
              items={dashboardItems}
            />
          </Box>
        </Drawer>
        <AppContent>
          <Navbar onDrawerToggle={handleDrawerToggle} />
          <OrgStatusGuard>
          <ErrorBoundary>
            <MainContent p={isLgUp ? 12 : 5}>
              <Box
                sx={{
                  flexGrow: 1,
                  flexShrink: 1,
                  flexBasis: 0,
                  minHeight: 0,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Outlet />
              </Box>
            </MainContent>
          </ErrorBoundary>
          </OrgStatusGuard>
          <Footer />
        </AppContent>

        <Settings />
      </DndProvider>
    </Root>
  );
};

// export default Dashboard;
export const Frame: FunctionComponent<PropsWithChildren<{ minimum?: boolean; title: string }>> = ({
  children: _children,
  minimum,
}) => {
  if (minimum) {
    return (
      <MuiThemeProvider>
        <Root>
          <ErrorBoundary>
            <SubscriptionGuard>
              <ViewScopeGuard>
                <CssBaseline />
              <GlobalStyle minimal />
              <Outlet />
            </ViewScopeGuard>
            </SubscriptionGuard>
          </ErrorBoundary>
        </Root>
      </MuiThemeProvider>
    );
  }
  return (
    <MuiThemeProvider>
      <LicenseAgreement />
      <FrameCore>
        <ErrorBoundary>
          <SubscriptionGuard>
          <ViewScopeGuard>
            <Outlet />
          </ViewScopeGuard>
          </SubscriptionGuard>
        </ErrorBoundary>
      </FrameCore>
    </MuiThemeProvider>
  );
};
